import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Sidebar,
  Spinner,
  Text,
  Title,
} from '@nimbus-ds/components';
import { Page } from '@nimbus-ds/patterns';
import { useLocation } from 'react-router';
import { Country } from '@tiendanube/common/src/enums';
import { useNavegate } from 'App/hooks';
import { useToast } from 'commons/hooks';
import { useGetCountry } from 'domains/Auth/hooks';
import {
  trackingNuvemEnvioPrintLabels,
  trackingNuvemEnvioPrintLabelsError,
  trackingNuvemEnvioPrintLabelsSuccess,
} from 'domains/NuvemEnvioCommons/tracking';
import { ORDERS_SECTION_ROUTES } from 'domains/Orders';
import { useStoreInfo } from 'domains/PartnersApps/hooks';
import { useGetNuvemEnvioConfigs } from 'domains/Shipping/DeliveryMethods/hooks';
import useNuvemEnvioTranslations from '../../../hooks/useNuvemEnvioTranslations/useNuvemEnvioTranslations';
import useCreateNuvemEnvioLabels from '../../hooks/useCreateNuvemEnvioLabels/useCreateNuvemEnvioLabels';
import { usePolling } from '../../hooks/usePolling/usePolling';
import useSimulateNuvemEnvioLabels from '../../hooks/useSimulateNuvemEnvioLabels/useSimulateNuvemEnvioLabels';
import ordersService from '../../ordersService/ordersService';
import {
  NE_POOLING_LABELS_INTERVAL,
  NE_POOLING_LABELS_TIMEOUT,
} from '../../utils/pooling';

function OrdersLabelGenerateStatusPage(): JSX.Element {
  const { id: storeId } = useStoreInfo();
  const t = useNuvemEnvioTranslations();
  const { isPostPaid, status: nuvemEnvioConfigsStatus } =
    useGetNuvemEnvioConfigs();
  const { createNuvemEnvioLabels, resetNuvemEnvioLabels, data, status } =
    useCreateNuvemEnvioLabels();
  const location = useLocation();
  const country = useGetCountry();

  const { goTo } = useNavegate();
  const { addToast } = useToast();

  const searchParams = new URLSearchParams(location.search);

  const [shouldShowLoading, setShouldShowLoading] = useState(true);

  const idsParam = searchParams.get('ids');
  const ids = useMemo(() => (idsParam ? idsParam.split(',') : []), [idsParam]);

  const {
    data: simulateNuvemEnvioLabelsData,
    status: simulateNuvemEnvioLabelsStatus,
  } = useSimulateNuvemEnvioLabels();

  const shouldStartPolling =
    nuvemEnvioConfigsStatus.isSuccess &&
    !isPostPaid &&
    (simulateNuvemEnvioLabelsStatus.isIdle ||
      !!simulateNuvemEnvioLabelsData?.balanceInfo?.shouldRecharge);

  const shouldCreateLabels =
    nuvemEnvioConfigsStatus.isSuccess &&
    ids.length &&
    (isPostPaid ||
      (!simulateNuvemEnvioLabelsData?.balanceInfo?.shouldRecharge &&
        simulateNuvemEnvioLabelsStatus.isSuccess));

  usePolling(
    shouldStartPolling,
    async () => {
      const response = await ordersService.simulateNuvemEnvioLabels({
        fulfillmentOrderIds: ids,
      });

      return !response?.balanceInfo?.shouldRecharge;
    },
    {
      interval: NE_POOLING_LABELS_INTERVAL,
      timeout: NE_POOLING_LABELS_TIMEOUT,
      onSuccess: () =>
        createNuvemEnvioLabels({
          createFile: {
            label: true,
            contentDeclaration: country === Country.BR,
          },
          fulfillmentOrderIds: ids,
        }),
      onTimeout: () => {
        setShouldShowLoading(false);
      },
    },
  );
  //   if (nuvemEnvioConfigsStatus.isSuccess && isPostPaid) return;

  //   let interval: NodeJS.Timeout;
  //   let timeout: NodeJS.Timeout;

  //   const startPolling = () => {
  //     timeout = setTimeout(() => {
  //       clearInterval(interval);
  //       setShouldShowLoading(false);
  //     }, NE_POOLING_LABELS_TIMEOUT);

  //     interval = setInterval(async () => {
  //       const response = await ordersService.simulateNuvemEnvioLabels({
  //         fulfillmentOrderIds: ids,
  //       });

  //       if (response?.balanceInfo?.shouldRecharge === false) {
  //         createNuvemEnvioLabels({
  //           createFile: {
  //             label: true,
  //             contentDeclaration: country === Country.BR,
  //           },
  //           fulfillmentOrderIds: ids,
  //         });

  //         clearInterval(interval);
  //         clearTimeout(timeout);
  //       }
  //     }, NE_POOLING_LABELS_INTERVAL);
  //   };

  //   startPolling();

  //   return () => {
  //     if (interval) clearInterval(interval);
  //     if (timeout) clearTimeout(timeout);
  //   };
  // }, [
  //   country,
  //   createNuvemEnvioLabels,
  //   ids,
  //   isPostPaid,
  //   setShouldShowLoading,
  //   nuvemEnvioConfigsStatus.isSuccess,
  // ]);

  useEffect(() => {
    if (shouldCreateLabels) {
      createNuvemEnvioLabels({
        createFile: {
          label: true,
          contentDeclaration: country === Country.BR ? true : false,
        },
        fulfillmentOrderIds: ids,
      });
    }

    resetNuvemEnvioLabels();
  }, [
    ids,
    createNuvemEnvioLabels,
    resetNuvemEnvioLabels,
    country,
    shouldCreateLabels,
  ]);

  useEffect(() => {
    if (status.isSuccess) {
      trackingNuvemEnvioPrintLabels(storeId);
      const openInNewTab = (url: string) => {
        window.open(url, '_blank', 'noopener,noreferrer');
      };

      data?.contentDeclarationUrls?.forEach(openInNewTab);
      data?.labelUrls?.forEach(openInNewTab);

      if (status.isError || (data?.errors && data?.errors.length > 0)) {
        trackingNuvemEnvioPrintLabelsError(storeId);
        addToast({
          label: t('printNuvemEnvioLabelsStatus.toast.error'),
          appearance: 'danger',
        });
      } else {
        trackingNuvemEnvioPrintLabelsSuccess(storeId);
        addToast({
          label: t('printNuvemEnvioLabelsStatus.toast.success'),
          appearance: 'success',
        });
      }

      resetNuvemEnvioLabels();
      goTo(ORDERS_SECTION_ROUTES.orderList);
    } else if (status?.isError) {
      resetNuvemEnvioLabels();

      addToast({
        label: t('printNuvemEnvioLabelsStatus.toast.error'),
        appearance: 'danger',
      });

      goTo(ORDERS_SECTION_ROUTES.orderList);
    }
  }, [status, goTo, data, addToast, t, resetNuvemEnvioLabels, storeId]);

  return (
    <Page>
      <Page.Body height="90vh" display="flex" alignItems="center">
        {shouldShowLoading && (
          <Box
            m="auto"
            width={{
              md: '100%',
              xs: '340px',
            }}
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap="4"
          >
            <Spinner size="large" color="neutral-textLow" />
            <Title as="h4">
              {t('printNuvemEnvioLabelsStatus.processing.title')}
            </Title>
            <Text textAlign="center">
              {t('printNuvemEnvioLabelsStatus.processing.description')}
            </Text>
          </Box>
        )}

        {!shouldShowLoading && (
          <Sidebar
            padding="base"
            position="right"
            open
            zIndex="800"
            maxWidth={{ xs: '100%', md: '375px' }}
          >
            <Sidebar.Header>
              <Title>{t('creditRechargeNuvemEnvioLabels.sidebar.title')}</Title>
            </Sidebar.Header>
            <Sidebar.Body>
              <Box
                display="flex"
                flexDirection="column"
                gap="4"
                alignItems="flex-end"
              >
                <Text>{t('creditRechargeNuvemEnvioLabels.sidebar.text')}</Text>

                <Button
                  onClick={() => goTo(ORDERS_SECTION_ROUTES.orderList)}
                  appearance="primary"
                >
                  {t('creditRechargeNuvemEnvioLabels.sidebar.button')}
                </Button>
              </Box>
            </Sidebar.Body>
          </Sidebar>
        )}
      </Page.Body>
    </Page>
  );
}

export default OrdersLabelGenerateStatusPage;
